import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";

const CATEGORIES = [
  "Sweet Shop", "Grocery", "Pharmacy", "Clothing", "Electronics",
  "Jewellery", "Furniture", "Restaurant", "Bakery", "Salon & Beauty",
  "Hardware", "Stationery", "Medical Store", "Mobile & Accessories",
  "Home Decor", "Sports & Fitness", "Toys", "Books", "Optician", "Other"
];

const CITIES = [
  "Lucknow", "Delhi", "Mumbai", "Bengaluru", "Hyderabad",
  "Chennai", "Kolkata", "Pune", "Jaipur", "Ahmedabad",
  "Surat", "Kanpur", "Nagpur", "Indore", "Bhopal", "Other"
];

const C = {
  bg: "#f9f9f7", card: "#ffffff", border: "#e5e5e5",
  accent: "#111", text: "#111", muted: "#888",
  error: "#cc0000", success: "#166534",
  green: "#25D366", accentLight: "#FF6B35",
};

export default function SignupPage() {
  const navigate = useNavigate();

  // steps: phone → otp → wizard → pin → done
  const [step, setStep] = useState("phone");

  // Phone + OTP
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [confirmResult, setConfirmResult] = useState(null);
  const [idToken, setIdToken] = useState(null); // saved after OTP verify
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState("");

  // Wizard
  const [businessName, setBusinessName] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // PIN setup
  const [newPin, setNewPin] = useState(["", "", "", ""]);
  const [confirmPin, setConfirmPin] = useState(["", "", "", ""]);
  const [settingPin, setSettingPin] = useState(false);

  // Result
  const [slug, setSlug] = useState("");
  const [liveUrl, setLiveUrl] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [businessNameSaved, setBusinessNameSaved] = useState("");

  const recaptchaVerifierRef = useRef(null);
  const otpRefs = useRef([]);
  const newPinRefs = useRef([]);
  const confirmPinRefs = useRef([]);

  // Pre-fill phone if coming from LoginPage
  useEffect(() => {
    const savedPhone = localStorage.getItem("qkard_signup_phone");
    const savedToken = localStorage.getItem("qkard_signup_token");
    if (savedPhone && savedToken) {
      const digits = savedPhone.replace("+91", "");
      setPhone(digits);
      setIdToken(savedToken);
      localStorage.removeItem("qkard_signup_phone");
      localStorage.removeItem("qkard_signup_token");
      setStep("wizard"); // skip phone+OTP — already verified
    }
  }, []);

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // ── Send OTP ─────────────────────────────────────────────────────
  async function handleSendOTP() {
    setError("");
    const cleaned = phone.replace(/\s/g, "");
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      setError("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setSending(true);
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible", callback: () => { },
      });
      await recaptchaVerifierRef.current.render();
      const result = await signInWithPhoneNumber(auth, `+91${cleaned}`, recaptchaVerifierRef.current);
      setConfirmResult(result);
      setStep("otp");
      setResendTimer(30);
    } catch (e) {
      console.error(e);
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
      setError("Could not send OTP. Please try again.");
    } finally {
      setSending(false);
    }
  }

  // ── Verify OTP ───────────────────────────────────────────────────
  async function handleVerifyOTP() {
    const code = otp.join("");
    if (code.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    setError(""); setVerifying(true);
    try {
      const cred = await confirmResult.confirm(code);
      const token = await cred.user.getIdToken();
      setIdToken(token);

      // Check if already registered — send them to login
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: token }),
      });
      const data = await res.json();
      if (data.isNewUser === false) {
        // Already has account — save and redirect to dashboard
        localStorage.setItem("qkard_businessId", data.business.businessId);
        localStorage.setItem("qkard_businessName", data.business.businessName);
        localStorage.setItem("qkard_phone", data.phone);
        navigate("/dashboard", { replace: true });
        return;
      }
      setStep("wizard");
    } catch (e) {
      console.error(e);
      setError("Invalid OTP. Please check and try again.");
    } finally {
      setVerifying(false);
    }
  }

  // ── Create Shop ──────────────────────────────────────────────────
  async function handleCreateShop() {
    if (!businessName.trim()) { setError("Shop name is required"); return; }
    if (!category) { setError("Please select a category"); return; }
    if (!city) { setError("Please select your city"); return; }
    setSaving(true); setError("");
    try {
      const fullPhone = `+91${phone.replace(/\s/g, "")}`;
      const res = await fetch(`${API}/shops`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: fullPhone,
          businessName: businessName.trim(),
          category, city,
          whatsappNumber: whatsapp || fullPhone.replace("+91", ""),
          description: description.trim(),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed");

      // Save for done screen
      setSlug(data.slug);
      setLiveUrl(data.liveUrl || `https://qkard.app/c/${data.slug}`);
      setBusinessId(data.businessId);
      setBusinessNameSaved(businessName.trim());

      // Also upsert SyncFieldUsers via verify-otp
      if (idToken) {
        await fetch(`${API}/auth/verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
      }

      // Go to PIN setup
      setStep("pin");
    } catch (e) {
      console.error(e);
      setError("Could not create your shop. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // ── Set PIN ──────────────────────────────────────────────────────
  async function handleSetPin() {
    setError("");
    const p1 = newPin.join("");
    const p2 = confirmPin.join("");
    if (p1.length !== 4) { setError("Enter a 4-digit PIN."); return; }
    if (p1 !== p2) {
      setError("PINs don't match. Try again.");
      setConfirmPin(["", "", "", ""]);
      setTimeout(() => confirmPinRefs.current[0]?.focus(), 100);
      return;
    }
    setSettingPin(true);
    try {
      const res = await fetch(`${API}/auth/set-pin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken, pin: p1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to set PIN");
      setStep("done");
    } catch (e) {
      setError(e.message || "Failed to set PIN. You can set it later from dashboard.");
    } finally {
      setSettingPin(false);
    }
  }

  function handleSkipPin() {
    // Save to localStorage and go to dashboard directly
    localStorage.setItem("qkard_businessId", businessId);
    localStorage.setItem("qkard_businessName", businessNameSaved);
    localStorage.setItem("qkard_phone", `+91${phone.replace(/\s/g, "")}`);
    navigate("/dashboard", { replace: true });
  }

  // ── PIN input helper ─────────────────────────────────────────────
  const handlePinDigit = (val, idx, arr, setArr, refs, onComplete) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...arr]; next[idx] = v; setArr(next);
    if (v && idx < 3) refs.current[idx + 1]?.focus();
    if (!v && idx > 0) refs.current[idx - 1]?.focus();
    if (onComplete && next.every(d => d)) onComplete(next.join(""));
  };

  // ── OTP helpers ──────────────────────────────────────────────────
  function handleOtpChange(index, value) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp]; next[index] = value.slice(-1); setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  }
  function handleOtpKeyDown(index, e) {
    if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "Enter" && otp.join("").length === 6) handleVerifyOTP();
  }
  function handleOtpPaste(e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) { setOtp(pasted.split("")); otpRefs.current[5]?.focus(); }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <div id="recaptcha-container" />

      <div style={s.card}>
        <div style={s.logo}>qk<span style={{ color: C.accentLight }}>a</span>rd</div>
        <div style={s.tagline}>Your digital catalogue, live in 5 minutes</div>

        {/* Step dots */}
        {["phone", "otp", "wizard", "pin"].includes(step) && (
          <div style={s.stepDots}>
            {["phone", "otp", "wizard", "pin"].map((st, i) => (
              <div key={st} style={{
                ...s.dot,
                ...((["phone", "otp", "wizard", "pin"].indexOf(step) >= i) ? s.dotActive : {}),
              }} />
            ))}
          </div>
        )}

        {/* ── PHONE ── */}
        {step === "phone" && (
          <>
            <label style={s.label}>Your mobile number</label>
            <div style={s.phoneRow}>
              <div style={s.flag}>🇮🇳 +91</div>
              <input
                style={{ ...s.input, flex: 1 }} type="tel"
                placeholder="98765 43210" value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={e => e.key === "Enter" && handleSendOTP()}
                maxLength={10} autoFocus
              />
            </div>
            <div style={s.hint}>OTP will be sent to this number</div>
            {error && <div style={s.error}>{error}</div>}
            <button style={{ ...s.btn, ...(sending ? s.btnDisabled : {}) }}
              onClick={handleSendOTP} disabled={sending}>
              {sending ? "Sending OTP..." : "Send OTP →"}
            </button>
            <div style={s.switchText}>
              Already have an account?{" "}
              <span style={s.link} onClick={() => navigate("/login")}>Sign in</span>
            </div>
          </>
        )}

        {/* ── OTP ── */}
        {step === "otp" && (
          <>
            <div style={s.stepTitle}>Enter OTP</div>
            <div style={s.stepSub}>Sent to +91 {phone}</div>
            {error && <div style={s.error}>{error}</div>}
            <div style={s.otpRow} onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input key={i} ref={el => otpRefs.current[i] = el}
                  style={{ ...s.otpBox, borderColor: digit ? "#111" : C.border }}
                  type="tel" maxLength={1} value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            <button style={{ ...s.btn, ...(verifying ? s.btnDisabled : {}) }}
              onClick={handleVerifyOTP} disabled={verifying}>
              {verifying ? "Verifying..." : "Verify OTP →"}
            </button>
            <div style={s.switchText}>
              {resendTimer > 0
                ? `Resend OTP in ${resendTimer}s`
                : <span style={s.link} onClick={async () => {
                  setOtp(["", "", "", "", "", ""]); setError("");
                  recaptchaVerifierRef.current?.clear();
                  recaptchaVerifierRef.current = null;
                  await handleSendOTP();
                }}>Resend OTP</span>
              }
              {" · "}
              <span style={s.link} onClick={() => { setStep("phone"); setOtp(["", "", "", "", "", ""]); setError(""); }}>
                Change number
              </span>
            </div>
          </>
        )}

        {/* ── WIZARD ── */}
        {step === "wizard" && (
          <>
            <div style={s.stepTitle}>Set up your shop</div>
            <div style={s.stepSub}>Just a few details to get started</div>
            {error && <div style={s.error}>{error}</div>}

            <label style={s.label}>Shop / Business Name *</label>
            <input style={s.input} placeholder="e.g. Sharma Dairy, Priya Boutique"
              value={businessName} onChange={e => setBusinessName(e.target.value)} autoFocus />

            <label style={{ ...s.label, marginTop: 16 }}>Category *</label>
            <select style={s.select} value={category}
              onChange={e => setCategory(e.target.value)}>
              <option value="">Select category...</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label style={{ ...s.label, marginTop: 16 }}>City *</label>
            <select style={s.select} value={city}
              onChange={e => setCity(e.target.value)}>
              <option value="">Select city...</option>
              {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label style={{ ...s.label, marginTop: 16 }}>WhatsApp Number</label>
            <input style={s.input} placeholder={`Default: ${phone}`} type="tel"
              maxLength={10} value={whatsapp}
              onChange={e => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 10))} />

            <label style={{ ...s.label, marginTop: 16 }}>Description <span style={{ fontWeight: 400, color: C.muted }}>(optional)</span></label>
            <textarea style={{ ...s.input, height: 72, resize: "vertical", fontFamily: "inherit" }}
              placeholder="What do you sell? e.g. Fresh dairy products, homemade sweets..."
              value={description} onChange={e => setDescription(e.target.value)} />

            <button style={{ ...s.btn, marginTop: 24, ...(saving ? s.btnDisabled : {}) }}
              onClick={handleCreateShop} disabled={saving}>
              {saving ? "Creating your shop..." : "Create My Shop →"}
            </button>
          </>
        )}

        {/* ── PIN SETUP ── */}
        {step === "pin" && (
          <>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>🔐</div>
            <div style={s.stepTitle}>Set a quick-login PIN</div>
            <div style={s.stepSub}>
              4-digit PIN for faster logins — no OTP needed next time
            </div>
            {error && <div style={s.error}>{error}</div>}

            <label style={s.label}>New PIN</label>
            <div style={s.pinRow}>
              {newPin.map((digit, i) => (
                <input key={i} ref={el => newPinRefs.current[i] = el}
                  style={{ ...s.pinBox, borderColor: digit ? C.accent : C.border }}
                  type="password" inputMode="numeric" maxLength={1} value={digit}
                  onChange={e => handlePinDigit(e.target.value, i, newPin, setNewPin, newPinRefs,
                    () => setTimeout(() => confirmPinRefs.current[0]?.focus(), 100)
                  )}
                  onKeyDown={e => { if (e.key === "Backspace" && !newPin[i] && i > 0) newPinRefs.current[i - 1]?.focus(); }}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            <label style={{ ...s.label, marginTop: 16 }}>Confirm PIN</label>
            <div style={s.pinRow}>
              {confirmPin.map((digit, i) => (
                <input key={i} ref={el => confirmPinRefs.current[i] = el}
                  style={{ ...s.pinBox, borderColor: digit ? C.accent : C.border }}
                  type="password" inputMode="numeric" maxLength={1} value={digit}
                  onChange={e => handlePinDigit(e.target.value, i, confirmPin, setConfirmPin, confirmPinRefs)}
                  onKeyDown={e => {
                    if (e.key === "Backspace" && !confirmPin[i] && i > 0) confirmPinRefs.current[i - 1]?.focus();
                    if (e.key === "Enter" && confirmPin.join("").length === 4) handleSetPin();
                  }}
                />
              ))}
            </div>

            <button style={{ ...s.btn, marginTop: 24, ...(settingPin ? s.btnDisabled : {}) }}
              onClick={handleSetPin} disabled={settingPin}>
              {settingPin ? "Saving PIN..." : "Save PIN & Continue →"}
            </button>
            <div style={{ ...s.switchText, marginTop: 12 }}>
              <span style={{ ...s.link, color: C.muted }} onClick={handleSkipPin}>
                Skip for now
              </span>
            </div>
          </>
        )}

        {/* ── DONE ── */}
        {step === "done" && (
          <>
            <div style={s.successIcon}>🎉</div>
            <div style={{ ...s.stepTitle, marginBottom: 6 }}>You're live!</div>
            <div style={{ ...s.stepSub, marginBottom: 20 }}>
              Your catalogue is ready to share
            </div>
            <div style={s.liveUrl}>{liveUrl}</div>
            <button style={s.shareBtn} onClick={() => {
              window.open(`https://wa.me/?text=${encodeURIComponent(`Check out my catalogue: ${liveUrl}`)}`, "_blank");
            }}>
              📤 Share on WhatsApp
            </button>
            <button style={s.btn} onClick={() => {
              localStorage.setItem("qkard_businessId", businessId);
              localStorage.setItem("qkard_businessName", businessNameSaved);
              localStorage.setItem("qkard_phone", `+91${phone.replace(/\s/g, "")}`);
              navigate("/dashboard", { replace: true });
            }}>
              Go to Dashboard →
            </button>
          </>
        )}

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: "100vh", background: C.bg, display: "flex",
    alignItems: "center", justifyContent: "center",
    padding: "24px 16px", fontFamily: "'Inter', sans-serif",
  },
  card: {
    background: C.card, borderRadius: 20, padding: "40px 36px",
    width: "100%", maxWidth: 440, boxShadow: "0 4px 32px rgba(0,0,0,0.08)",
  },
  logo: { fontSize: 28, fontWeight: 800, color: C.text, letterSpacing: "-1px", marginBottom: 4 },
  tagline: { fontSize: 14, color: C.muted, marginBottom: 24 },
  stepDots: { display: "flex", gap: 6, justifyContent: "center", marginBottom: 28 },
  dot: { width: 8, height: 8, borderRadius: 4, background: "#e5e5e5", transition: "all 0.2s" },
  dotActive: { background: C.accent, width: 24 },
  stepTitle: { fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 6, textAlign: "center" },
  stepSub: { fontSize: 14, color: C.muted, marginBottom: 20, textAlign: "center", lineHeight: 1.5 },
  label: { fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 6, display: "block", marginTop: 4 },
  hint: { fontSize: 12, color: "#999", marginTop: 8, marginBottom: 20 },
  input: {
    width: "100%", padding: "12px 16px", borderRadius: 12,
    border: `1.5px solid ${C.border}`, fontSize: 15, outline: "none",
    boxSizing: "border-box", fontFamily: "inherit", marginBottom: 0,
  },
  select: {
    width: "100%", padding: "12px 16px", borderRadius: 12,
    border: `1.5px solid ${C.border}`, fontSize: 15, outline: "none",
    boxSizing: "border-box", background: "#fff", color: C.text,
  },
  btn: {
    width: "100%", padding: "14px", borderRadius: 12, border: "none",
    background: C.accent, color: "#fff", fontSize: 16, fontWeight: 600,
    cursor: "pointer", marginTop: 8, transition: "background 0.2s",
  },
  btnDisabled: { background: "#ccc", cursor: "not-allowed" },
  error: {
    background: "#fff0f0", color: C.error, padding: "10px 14px",
    borderRadius: 10, fontSize: 13, marginBottom: 16,
  },
  phoneRow: { display: "flex", gap: 8 },
  flag: {
    padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${C.border}`,
    fontSize: 15, background: "#f5f5f5", color: "#444", whiteSpace: "nowrap",
  },
  otpRow: { display: "flex", gap: 8, justifyContent: "center", margin: "20px 0" },
  otpBox: {
    width: 46, height: 54, borderRadius: 12, border: `2px solid ${C.border}`,
    fontSize: 22, fontWeight: 700, textAlign: "center", outline: "none",
    transition: "border-color 0.2s",
  },
  pinRow: { display: "flex", gap: 12, justifyContent: "center", marginBottom: 8 },
  pinBox: {
    width: 56, height: 62, textAlign: "center", fontSize: 22, fontWeight: 700,
    background: "#f9f9f7", border: `2px solid ${C.border}`, borderRadius: 12,
    color: C.text, outline: "none", transition: "border-color 0.15s",
  },
  switchText: { textAlign: "center", marginTop: 16, fontSize: 13, color: C.muted },
  link: { color: C.accent, fontWeight: 600, cursor: "pointer" },
  successIcon: { fontSize: 56, textAlign: "center", marginBottom: 16 },
  liveUrl: {
    background: "#f0f9f0", border: "1.5px solid #22c55e", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, fontWeight: 600, color: C.success,
    textAlign: "center", wordBreak: "break-all", marginBottom: 16,
  },
  shareBtn: {
    width: "100%", padding: "14px", borderRadius: 12, border: "none",
    background: C.green, color: "#fff", fontSize: 16, fontWeight: 600,
    cursor: "pointer", marginBottom: 10,
  },
};